(function (root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.YGVariantAvailability = api;
    if (root.document) api.start(root.document);
  }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  const normalize = (value) =>
    String(value || '')
      .trim()
      .toLowerCase();

  const variantMatches = (variant, selectedByPosition) =>
    Object.entries(selectedByPosition).every(([position, selectedName]) => {
      const index = Number(position) - 1;
      return normalize(variant.options?.[index]) === normalize(selectedName);
    });

  const resolveVariantState = ({ variants, colorPosition, selectedByPosition }) => {
    const colorName = normalize(selectedByPosition?.[colorPosition]);
    const colorIndex = Number(colorPosition) - 1;
    const colorVariants = colorName
      ? (variants || []).filter((variant) => normalize(variant.options?.[colorIndex]) === colorName)
      : [];
    const validNamesByPosition = new Map();

    colorVariants.forEach((variant) => {
      (variant.options || []).forEach((name, index) => {
        const position = index + 1;
        if (position === Number(colorPosition)) return;
        if (!validNamesByPosition.has(position)) validNamesByPosition.set(position, new Set());
        validNamesByPosition.get(position).add(normalize(name));
      });
    });

    const exactVariant = colorVariants.find((variant) => variantMatches(variant, selectedByPosition || {})) || null;
    const targetVariant =
      exactVariant ||
      colorVariants.find((variant) => variant.available !== false) ||
      colorVariants[0] ||
      null;

    return {
      colorVariants,
      exactVariant,
      targetVariant,
      validNamesByPosition,
    };
  };

  const parseVariantMap = (picker) => {
    try {
      return JSON.parse(picker.querySelector('script[data-variant-map]')?.textContent || '[]');
    } catch (error) {
      return [];
    }
  };

  const optionElements = (input) => {
    const picker = input.closest('variant-picker');
    const elements = [];

    if (picker && input.id) {
      const escapedId =
        window.CSS && typeof window.CSS.escape === 'function'
          ? window.CSS.escape(input.id)
          : input.id.replace(/"/g, '\\"');
      picker.querySelectorAll(`label[for="${escapedId}"]`).forEach((label) => elements.push(label));
    }

    const wrappingOption = input.closest('.popover-listbox__option');
    if (wrappingOption && !elements.includes(wrappingOption)) elements.push(wrappingOption);
    if (!elements.length && input.nextElementSibling) elements.push(input.nextElementSibling);

    return elements;
  };

  const optionName = (input) =>
    normalize(input.getAttribute('data-option-value-name') || optionElements(input)[0]?.textContent);

  const selectedByPosition = (inputs) => {
    const selected = {};
    inputs.forEach((input) => {
      if (input.checked) selected[input.getAttribute('data-option-position')] = optionName(input);
    });
    return selected;
  };

  const inputForVariantFallback = (inputs, variant, colorPos, previousSelection) => {
    if (!variant) return null;

    return (
      (variant.options || [])
        .map((name, index) => {
          const position = index + 1;
          if (
            position === Number(colorPos) ||
            normalize(previousSelection?.[position]) === normalize(name)
          ) {
            return null;
          }

          return inputs.find(
            (input) =>
              Number(input.getAttribute('data-option-position')) === position &&
              optionName(input) === normalize(name)
          );
        })
        .find(Boolean) || null
    );
  };

  const colorPosition = (picker) =>
    picker
      .querySelector('fieldset:has(.color-swatch) input[data-option-position]')
      ?.getAttribute('data-option-position') || '';

  const setLegend = (input) => {
    const fieldset = input.closest('fieldset');
    const displayName = (
      input.getAttribute('data-option-value-name') ||
      optionElements(input)[0]?.textContent ||
      ''
    )
      .replace(/\s+/g, ' ')
      .trim();
    if (!fieldset || !displayName) return;

    const legend = fieldset.querySelector('.variant-picker__option-info span');
    const dropdown = fieldset.querySelector('.select span, [id$="-selected-value"]');
    if (legend) legend.textContent = displayName;
    if (dropdown) dropdown.textContent = displayName;
  };

  const setUnavailable = (input, unavailable) => {
    input.tabIndex = unavailable ? -1 : 0;
    input.setAttribute('aria-disabled', unavailable ? 'true' : 'false');

    optionElements(input).forEach((element) => {
      if (!element.dataset.ygNativeDisabled) {
        element.dataset.ygNativeDisabled = String(
          element.classList.contains('is-disabled') && !element.classList.contains('is-unavailable')
        );
      }

      const nativeDisabled = element.dataset.ygNativeDisabled === 'true';
      element.hidden = false;
      element.removeAttribute('hidden');
      element.classList.toggle('is-unavailable', unavailable);
      element.classList.toggle('is-disabled', nativeDisabled || unavailable);

      if (unavailable || nativeDisabled) {
        element.setAttribute('aria-disabled', 'true');
      } else {
        element.removeAttribute('aria-disabled');
      }
    });
  };

  const selectInput = (group, target) => {
    group.forEach((input) => {
      input.checked = input === target;
      optionElements(input).forEach((element) => element.classList.toggle('is-selected', input === target));
    });
    if (target) setLegend(target);
  };

  const rubikLabelFor = (rootNode, input) =>
    Array.from(rootNode?.querySelectorAll?.('label[for]') || []).find(
      (label) => label.getAttribute('for') === input?.id
    );

  const selectRubikInput = (rootNode, group, target) => {
    group.forEach((input) => {
      input.checked = input === target;
      if (input === target) {
        input.setAttribute?.('checked', '');
      } else {
        input.removeAttribute?.('checked');
      }
    });

    const option = target?.closest?.('[data-rubik-option-index]');
    const legend = option?.querySelector?.('.rubik-swatch__option-name-value');
    const label = rubikLabelFor(rootNode, target);
    const value = label?.getAttribute?.('data-rubik-option-value') || label?.textContent?.trim();
    if (legend && value) legend.textContent = value;
  };

  const reconcileRubikHost = (host) => {
    const rootNode = host?.shadowRoot;
    const swatch = rootNode?.querySelector?.('.rubik-swatch');
    const targetVariantId = swatch?.getAttribute?.('data-rubik-alternative-variant-id');
    if (!rootNode || !targetVariantId || targetVariantId === 'null') return false;

    let changed = false;
    const inputs = Array.from(rootNode.querySelectorAll('input[name]'));
    const groups = new Map();

    inputs.forEach((input) => {
      if (!groups.has(input.name)) groups.set(input.name, []);
      groups.get(input.name).push(input);
    });

    groups.forEach((group) => {
      const target = group.find(
        (candidate) => candidate.getAttribute('data-rubik-variant-id') === targetVariantId
      );
      const current = group.find((candidate) => candidate.checked);
      if (!target || target === current) return;

      selectRubikInput(rootNode, group, target);
      changed = true;
    });

    return changed;
  };

  const syncPickerVariantInput = (picker, variant) => {
    const formId = picker?.getAttribute?.('form-id');
    const documentRef = picker?.ownerDocument;
    const form =
      documentRef?.getElementById?.(formId) ||
      documentRef?.forms?.namedItem?.(formId) ||
      documentRef?.forms?.[formId];
    const input = form?.elements?.namedItem?.('id');
    if (!input || !variant?.id) return;

    input.value = String(variant.id);
    input.disabled = false;
    input.removeAttribute?.('disabled');
  };

  const reconcilePicker = (picker) => {
    if (!picker?.hasAttribute('lock-unavailable')) return null;

    const variants = parseVariantMap(picker);
    const inputs = Array.from(picker.querySelectorAll('input[data-option-position]'));
    const colorPos = colorPosition(picker);
    if (!variants.length || !inputs.length || !colorPos) return null;

    const state = resolveVariantState({
      variants,
      colorPosition: Number(colorPos),
      selectedByPosition: selectedByPosition(inputs),
    });

    inputs.forEach((input) => {
      const position = Number(input.getAttribute('data-option-position'));
      if (position === Number(colorPos)) return;
      const validNames = state.validNamesByPosition.get(position) || new Set();
      setUnavailable(input, !validNames.has(optionName(input)));
    });

    if (state.targetVariant && !state.exactVariant) {
      state.targetVariant.options.forEach((name, index) => {
        const position = index + 1;
        if (position === Number(colorPos)) return;

        const group = inputs.filter(
          (input) => Number(input.getAttribute('data-option-position')) === position
        );
        const target = group.find((input) => optionName(input) === normalize(name));
        if (target) selectInput(group, target);
      });
    }

    syncPickerVariantInput(picker, state.targetVariant);
    return state.targetVariant;
  };

  const syncVariantInput = (documentRef, detail) => {
    const formReference = detail?.formId;
    const referencedForm = formReference?.form;
    const formId =
      typeof formReference === 'string'
        ? formReference
        : referencedForm?.getAttribute?.('id') || formReference?.getAttribute?.('form');
    const form =
      documentRef?.getElementById?.(formId) ||
      documentRef?.forms?.namedItem?.(formId) ||
      documentRef?.forms?.[formId] ||
      referencedForm;
    const input = form?.elements?.namedItem?.('id');
    if (!input || !detail?.variant?.id) return;

    input.value = String(detail.variant.id);
    input.disabled = false;
    input.removeAttribute?.('disabled');
  };

  const initWithin = (rootNode) => {
    if (rootNode.matches?.('variant-picker[lock-unavailable]')) reconcilePicker(rootNode);
    rootNode
      .querySelectorAll?.('variant-picker[lock-unavailable]')
      .forEach((picker) => reconcilePicker(picker));
  };

  const start = (documentRef) => {
    if (!documentRef || documentRef.documentElement.dataset.ygVariantAvailability === 'true') return;
    documentRef.documentElement.dataset.ygVariantAvailability = 'true';

    const init = () => initWithin(documentRef);
    if (documentRef.readyState === 'loading') {
      documentRef.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
      init();
    }

    documentRef.addEventListener(
      'click',
      (event) => {
        const unavailable = event.target.closest?.('variant-picker[lock-unavailable] .is-unavailable');
        if (!unavailable) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );

    documentRef.addEventListener(
      'change',
      (event) => {
        const input = event.target;
        if (!input?.matches?.('variant-picker[lock-unavailable] input[data-option-position]')) return;

        const picker = input.closest('variant-picker');
        const inputs = Array.from(picker.querySelectorAll('input[data-option-position]'));
        const colorPos = colorPosition(picker);
        const previousSelection = selectedByPosition(inputs);
        const targetVariant = reconcilePicker(picker);
        const fallbackInput = inputForVariantFallback(
          inputs,
          targetVariant,
          colorPos,
          previousSelection
        );

        if (!fallbackInput || typeof picker.selectCombination !== 'function') return;

        event.stopImmediatePropagation();
        picker.selectCombination({
          optionValues: inputs
            .filter((candidate) => candidate.checked)
            .sort(
              (a, b) =>
                Number(a.getAttribute('data-option-position')) -
                Number(b.getAttribute('data-option-position'))
            )
            .map((candidate) => candidate.value),
          productChange: fallbackInput.hasAttribute('data-product-url'),
        });
      },
      true
    );

    documentRef.addEventListener('variant:change', (event) => {
      syncVariantInput(documentRef, event.detail);
    });

    const observedRubikHosts = new WeakSet();
    const observeRubikHost = (host, attempt = 0) => {
      if (!host || observedRubikHosts.has(host)) return;

      const shadowRoot = host.shadowRoot;
      if (!shadowRoot) {
        if (attempt < 20) {
          documentRef.defaultView?.setTimeout?.(() => observeRubikHost(host, attempt + 1), 50);
        }
        return;
      }

      observedRubikHosts.add(host);
      reconcileRubikHost(host);
      new MutationObserver(() => reconcileRubikHost(host)).observe(shadowRoot, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-rubik-alternative-variant-id', 'data-rubik-variant-id'],
      });
    };

    const initRubikWithin = (rootNode) => {
      if (rootNode.matches?.('rubik-swatch')) observeRubikHost(rootNode);
      rootNode.querySelectorAll?.('rubik-swatch').forEach((host) => observeRubikHost(host));
    };

    initRubikWithin(documentRef);
    new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          initWithin(node);
          initRubikWithin(node);
        });
      });
    }).observe(documentRef.documentElement, { childList: true, subtree: true });
  };

  return {
    normalize,
    resolveVariantState,
    inputForVariantFallback,
    reconcilePicker,
    reconcileRubikHost,
    selectRubikInput,
    syncPickerVariantInput,
    syncVariantInput,
    start,
  };
});
